/**
 * Security Audit Logging System
 * Provides centralized audit logging for security events
 */

import { supabase, getProdSupabaseClient } from '@/lib/supabase/client';
import { logSecurityEvent } from './monitoring';

export interface AuditEvent {
  action: string;
  resource: string;
  resourceId?: string;
  metadata?: Record<string, unknown>;
  severity?: 'low' | 'medium' | 'high' | 'critical';
}

export interface PageAccessEvent {
  page: string;
  action: string;
  metadata?: Record<string, unknown>;
}

export interface DataAccessEvent {
  resource: string;
  action: string;
  resourceId?: string;
  filters?: Record<string, unknown>;
  count?: number;
  metadata?: Record<string, unknown>;
}

export interface SecurityViolationEvent {
  eventType: string;
  details: Record<string, unknown>;
  severity?: 'low' | 'medium' | 'high' | 'critical';
}

/**
 * Log page access events
 */
export async function logPageAccess(event: PageAccessEvent): Promise<void> {
  try {
    const {
      data: { user }
    } = await supabase.auth.getUser();
    if (!user) return;

    // Extract tenant from JWT
    const {
      data: { session }
    } = await supabase.auth.getSession();
    const tenantId = session?.access_token ? extractTenantFromJWT(session.access_token) : null;

    // Use prod client to access prod.security_audit_logs table
    const prodClient = getProdSupabaseClient();
    await prodClient.from('security_audit_logs').insert({
      user_id: user.id,
      tenant_id: tenantId,
      event_type: 'page_access',
      event_category: 'data_access', // Required field
      resource_type: 'page',
      resource_id: null, // Page access doesn't have a UUID resource_id - page name stored in metadata
      action: event.action,
      outcome: 'success', // Required field
      metadata: {
        page: event.page,
        action: event.action,
        ...event.metadata
      }
    });
  } catch (error) {
    console.error('Failed to log page access:', error);
    // Don't throw - audit logging should never break the app
  }
}

/**
 * Log data access events
 */
export async function logDataAccess(event: DataAccessEvent): Promise<void> {
  try {
    const {
      data: { user }
    } = await supabase.auth.getUser();
    if (!user) return;

    const {
      data: { session }
    } = await supabase.auth.getSession();
    const tenantId = session?.access_token ? extractTenantFromJWT(session.access_token) : null;

    // Use prod client to access prod.security_audit_logs table
    const prodClient = getProdSupabaseClient();
    await prodClient.from('security_audit_logs').insert({
      user_id: user.id,
      tenant_id: tenantId,
      event_type: 'data_access',
      event_category: 'data_access', // Required field
      resource_type: event.resource,
      resource_id: event.resourceId || null,
      action: event.action,
      outcome: 'success', // Required field
      metadata: {
        resource: event.resource,
        action: event.action,
        filters: event.filters,
        count: event.count,
        ...event.metadata
      }
    });
  } catch (error) {
    console.error('Failed to log data access:', error);
  }
}

/**
 * Log data export events
 */
export async function logDataExport(
  resource: string,
  filters: Record<string, unknown>,
  count: number
): Promise<void> {
  await logDataAccess({
    resource,
    action: 'export',
    filters,
    count,
    metadata: {
      exportType: 'csv',
      timestamp: new Date().toISOString()
    }
  });
}

/**
 * Log security violation events
 */
export async function logSecurityViolation(
  eventType: string,
  details: Record<string, unknown>
): Promise<void> {
  try {
    const {
      data: { user }
    } = await supabase.auth.getUser();

    const {
      data: { session }
    } = await supabase.auth.getSession();
    const tenantId = session?.access_token ? extractTenantFromJWT(session.access_token) : null;

    // Use prod client to access prod.security_audit_logs table
    const prodClient = getProdSupabaseClient();
    await prodClient.from('security_audit_logs').insert({
      user_id: user?.id || null,
      tenant_id: tenantId,
      event_type: 'security_violation',
      event_category: 'system', // Required field
      resource_type: 'security',
      resource_id: null,
      action: eventType,
      outcome: 'failure', // Required field - violations are failures
      metadata: {
        violationType: eventType,
        ...details,
        timestamp: new Date().toISOString()
      }
    });

    // Also log to security monitoring
    logSecurityEvent(eventType, {
      userId: user?.id,
      tenantId,
      ...details
    });
  } catch (error) {
    console.error('Failed to log security violation:', error);
  }
}

/**
 * Log field access events (for SecureField component)
 */
export async function logFieldAccess(
  field: string,
  permission: { resource: string; action: string },
  granted: boolean
): Promise<void> {
  if (!granted) {
    // Only log denied access attempts
    await logSecurityViolation('field_access_denied', {
      field,
      permission,
      granted
    });
  }
}

/**
 * Log component access events (for PermissionBoundary)
 */
export async function logComponentAccess(
  component: string,
  permissions: Array<{ resource: string; action: string }>,
  granted: boolean
): Promise<void> {
  if (!granted) {
    await logSecurityViolation('component_access_denied', {
      component,
      permissions,
      granted
    });
  }
}

/**
 * Log admin actions
 */
export async function logAdminAction(
  action: string,
  target: Record<string, unknown>,
  metadata?: Record<string, unknown>
): Promise<void> {
  try {
    const {
      data: { user }
    } = await supabase.auth.getUser();
    if (!user) return;

    const {
      data: { session }
    } = await supabase.auth.getSession();
    const tenantId = session?.access_token ? extractTenantFromJWT(session.access_token) : null;

    // Use prod client to access prod.security_audit_logs table
    const prodClient = getProdSupabaseClient();

    // Extract resource_type from target if available, otherwise use 'admin'
    const resourceType = (target.resource as string) || 'admin';
    // resource_id is TEXT type in the table, so we can pass the string directly
    const resourceId = (target.resource_id as string) || (target.technician_id as string) || null;

    const insertData = {
      user_id: user.id,
      tenant_id: tenantId,
      event_type: 'admin_action',
      event_category: 'authorization', // Required field
      resource_type: resourceType,
      resource_id: resourceId, // TEXT type, so string is fine
      action,
      outcome: 'success', // Required field
      metadata: {
        action,
        target,
        ...metadata,
        timestamp: new Date().toISOString()
      }
    };

    const { error: insertError } = await prodClient.from('security_audit_logs').insert(insertData);

    if (insertError) {
      // Log the actual error details to help debug
      console.error('Failed to log admin action:', {
        error: insertError,
        message: insertError.message,
        code: insertError.code,
        details: insertError.details,
        hint: insertError.hint,
        insertData
      });
    }
  } catch (error) {
    console.error('Failed to log admin action (catch):', error);
  }
}

/**
 * Extract tenant ID from JWT token
 */
function extractTenantFromJWT(token: string): string | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;

    const claims = JSON.parse(atob(payload));
    return claims.tenant_id || null;
  } catch (error) {
    console.error('Failed to extract tenant from JWT:', error);
    return null;
  }
}
